import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Alert,
  Paper,
} from '@mui/material'
import {
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Delete as DeleteIcon,

} from '@mui/icons-material'
import { request } from '../common/services/request'
import { formatLocalDateTimeNoSeconds } from '../common/utils'
import { API_ENDPOINTS } from '../common/endpoints'
import type { Schedule, ScheduleListResponse, ScheduleStatus, MetricType, DiseaseProfile } from '../common/types'

function Schedules({ compact = false }: { compact?: boolean } = {}) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [schedules, setSchedules] = useState<Schedule[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(compact ? 10 : 20)
  const [loadingMore, setLoadingMore] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null)
  const [editForm, setEditForm] = useState<Partial<Schedule>>({})
  
  const navigate = useNavigate()
  
  const DISEASE_PROFILES: Array<{ value: DiseaseProfile; label: string }> = [
    { value: 'normal', label: 'Normal' },
    { value: 'hypertension', label: 'Hypertension' },
    { value: 'copd', label: 'COPD' },
    { value: 'diabetes', label: 'Diabetes' },
  ]
  
  const METRIC_TYPES: Array<{ value: MetricType; label: string; unit: string }> = [
    { value: 'sbp', label: 'Systolic BP', unit: 'mmHg' },
    { value: 'dbp', label: 'Diastolic BP', unit: 'mmHg' },
    { value: 'hr', label: 'Heart Rate', unit: 'bpm' },
    { value: 'spo2', label: 'SpO2', unit: '%' },
    { value: 'rr', label: 'Respiratory Rate', unit: 'breaths/min' },
    { value: 'temp', label: 'Temperature', unit: '°C' },
    { value: 'glucose', label: 'Glucose', unit: 'mg/dL' },
  ]

  const fetchSchedules = async (pageNum = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }
      const client = request()
      const res = await client.get<ScheduleListResponse>(API_ENDPOINTS.SCHEDULES.BASE, {
        params: { page: pageNum, page_size: pageSize },
      })
      if (append) {
        setSchedules(prev => [...prev, ...res.data.items])
      } else {
        setSchedules(res.data.items)
      }
      setTotal(res.data.total)
      setPage(pageNum - 1)
    } catch (e: any) {
      setError(e.message || 'Failed to load schedules')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    fetchSchedules(page + 1, false)
  }, [pageSize])

  const handlePause = async (id: number) => {
    try {
      const client = request()
      await client.post(API_ENDPOINTS.SCHEDULES.PAUSE(id))
      setSuccess('Schedule paused')
      fetchSchedules()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to pause schedule')
    }
  }

  const handleResume = async (id: number) => {
    try {
      const client = request()
      await client.post(API_ENDPOINTS.SCHEDULES.RESUME(id))
      setSuccess('Schedule resumed')
      fetchSchedules()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to resume schedule')
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this schedule?')) return

    try {
      const client = request()
      await client.delete(API_ENDPOINTS.SCHEDULES.BY_ID(id))
      setSuccess('Schedule deleted')
      fetchSchedules()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to delete schedule')
    }
  }
  
  
  const handleRowClick = (schedule: Schedule) => {
    navigate(`/schedules/${schedule.id}`)
  }
  
  const handleSaveEdit = async () => {
    if (!selectedSchedule) return
    
    try {
      const client = request()
      await client.patch(API_ENDPOINTS.SCHEDULES.BY_ID(selectedSchedule.id), editForm)
      setSuccess('Schedule updated successfully')
      setEditDialogOpen(false)
      setSelectedSchedule(null)
      fetchSchedules()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to update schedule')
    }
  }

  const getStatusColor = (status: ScheduleStatus): 'success' | 'warning' | 'default' => {
    switch (status) {
      case 'active':
        return 'success'
      case 'paused':
        return 'warning'
      default:
        return 'default'
    }
  }

  return (
    <Box>
      {!compact && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                Schedules
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage automated vital generation schedules
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
        </>
      )}

      <TableContainer component={Paper}>
        <Box sx={compact ? { maxHeight: 500, overflow: 'auto' } : {}} onScroll={compact ? (e) => {
              const t = e.currentTarget as HTMLElement
              if (loadingMore) return
              if (total && schedules.length >= total) return
              const scrollBottom = t.scrollHeight - t.scrollTop - t.clientHeight
              if (scrollBottom < 50) {
                // near bottom -> load next page
                fetchSchedules(page + 2, true)
              }
            } : undefined}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead sx={compact ? { position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 } : {}}>
                <TableRow>
                  <TableCell sx={{ width: '8%' }}>Created</TableCell>
                  <TableCell sx={{ width: '28%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Name</TableCell>
                  <TableCell sx={{ width: '5%' }}>UUID</TableCell>
                  <TableCell sx={{ width: '8%' }}>Prof</TableCell>
                  <TableCell sx={{ width: '8%' }}>Stat</TableCell>
                  <TableCell sx={{ width: '15%' }}>Last Run</TableCell>
                  <TableCell sx={{ width: '14%' }}>Next Run</TableCell>
                  <TableCell sx={{ width: '6%' }}>Runs</TableCell>
                  <TableCell align="right" sx={{ width: '10%' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && schedules.length === 0 ? (
                  [1, 2, 3].map((i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={9}>
                        <Box
                          sx={{
                            bgcolor: '#f0f0f0',
                            height: 40,
                            borderRadius: 1,
                            '@keyframes pulse': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0.5 },
                            },
                            animation: 'pulse 1.5s ease-in-out infinite',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (schedules?.length ?? 0) === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="textSecondary">No schedules created</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  schedules.map((schedule) => (
                    <TableRow 
                      key={schedule.id} 
                      hover 
                      sx={{ '& td, & th': { py: 0.5 }, cursor: 'pointer' }}
                      onClick={() => handleRowClick(schedule)}
                    >
                      <TableCell sx={{ width: '8%' }}>
                        {formatLocalDateTimeNoSeconds(schedule.created_at)}
                      </TableCell>
                      <TableCell sx={{ width: '28%' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {schedule.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            Interval: {schedule.interval} · Window: {schedule.window_days ?? '-'}d · Dropout: {typeof schedule.dropout_rate !== 'undefined' ? schedule.dropout_rate : '0.0'} · Normal: {schedule.profile === 'normal' ? 'yes' : 'no'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: '5%' }}>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'monospace' }}
                          noWrap
                          title={schedule.openemr_patient_uuid}
                        >
                          {schedule.openemr_patient_uuid ? schedule.openemr_patient_uuid.slice(0, 5) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '8%' }}>
                        <Chip label={schedule.profile} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ width: '8%' }}>
                        <Chip
                          label={schedule.status}
                          size="small"
                          color={getStatusColor(schedule.status)}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '15%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" noWrap title={formatLocalDateTimeNoSeconds(schedule.last_run_at)}>
                          {formatLocalDateTimeNoSeconds(schedule.last_run_at)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '14%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" noWrap title={formatLocalDateTimeNoSeconds(schedule.next_run_at)}>
                          {formatLocalDateTimeNoSeconds(schedule.next_run_at)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '6%' }}>{schedule.run_count}</TableCell>
                      <TableCell align="right" sx={{ width: '10%' }}>
                        {schedule.status === 'active' ? (
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handlePause(schedule.id); }}
                            title="Pause"
                          >
                            <PauseIcon />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleResume(schedule.id); }}
                            title="Resume"
                            color="success"
                          >
                            <ResumeIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleDelete(schedule.id); }}
                          title="Delete"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {loadingMore && compact && (
              <Box display="flex" justifyContent="center" p={1}>
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
            {!compact && (
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10))
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 20, 50]}
              />
            )}
      </TableContainer>
      
      {/* Edit Schedule Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Schedule</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={editForm.status as any || 'active'}
                onChange={(e: any) => setEditForm({ ...editForm, status: e.target.value })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Profile"
                value={editForm.profile || 'normal'}
                onChange={(e) => setEditForm({ ...editForm, profile: e.target.value as DiseaseProfile })}
              >
                {DISEASE_PROFILES.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Interval"
                value={editForm.interval || '6h'}
                onChange={(e) => setEditForm({ ...editForm, interval: e.target.value })}
              >
                <MenuItem value="1m">Every 1 minute</MenuItem>
                <MenuItem value="10m">Every 10 minutes</MenuItem>
                <MenuItem value="30m">Every 30 minutes</MenuItem>
                <MenuItem value="1h">Every hour</MenuItem>
                <MenuItem value="6h">Every 6 hours</MenuItem>
                <MenuItem value="12h">Every 12 hours</MenuItem>
                <MenuItem value="1d">Every day</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Window Days"
                value={editForm.window_days || 7}
                onChange={(e) => setEditForm({ ...editForm, window_days: parseInt(e.target.value) || 7 })}
                inputProps={{ min: 1, max: 365 }}
                helperText="Days of historical data in sliding window"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Metrics"
                value={editForm.metrics || []}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    metrics: typeof e.target.value === 'string'
                      ? [e.target.value as MetricType]
                      : e.target.value as MetricType[],
                  })
                }
                SelectProps={{ multiple: true }}
              >
                {METRIC_TYPES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label} ({m.unit})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Seed"
                value={editForm.seed ?? ''}
                onChange={(e) =>
                  setEditForm({ ...editForm, seed: e.target.value ? parseInt(e.target.value) : undefined })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Noise Level"
                value={editForm.noise_level ?? 0.1}
                onChange={(e) => setEditForm({ ...editForm, noise_level: parseFloat(e.target.value) || 0.1 })}
                inputProps={{ min: 0, max: 1, step: 0.1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Dropout Rate"
                value={editForm.dropout_rate ?? 0.0}
                onChange={(e) => setEditForm({ ...editForm, dropout_rate: parseFloat(e.target.value) || 0.0 })}
                inputProps={{ min: 0, max: 1, step: 0.05 }}
                helperText="0 = no dropout, 1 = drop all"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Destination URL"
                value={editForm.destination_url || ''}
                onChange={(e) => setEditForm({ ...editForm, destination_url: e.target.value })}
                helperText="URL to forward generated vitals to"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Schedules
